import { useEffect, useId, useRef, type ReactNode } from 'react';

/**
 * Diálogo modal.
 *
 * Usa o `<dialog>` nativo em vez de uma sobreposição montada à mão. O navegador
 * já entrega prisão de foco, fechamento no Esc e inércia do resto da página —
 * três coisas que faltam na maioria dos modais caseiros e que quebram o uso por
 * teclado sem que ninguém perceba.
 *
 * O estado de aberto continua sendo do React: o `onCancel` cancela o fechamento
 * nativo e avisa quem chamou, para não existirem duas fontes de verdade.
 */
/**
 * Quantos diálogos estão abertos agora.
 *
 * É contagem, e não um booleano, porque um modal pode abrir outro — "Alterar
 * acordo" por cima da ficha. Se o de cima devolvesse a rolagem ao fechar, a
 * página voltaria a correr atrás do que ainda está aberto.
 */
let dialogosAbertos = 0;

export function Modal({
  aberto,
  aoFechar,
  titulo,
  descricao,
  children,
  rodape,
  largura = '42rem',
}: {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  descricao?: string;
  children: ReactNode;
  /** Ações fixas no rodapé — não rolam junto com o conteúdo. */
  rodape?: ReactNode;
  largura?: string;
}) {
  const referencia = useRef<HTMLDialogElement>(null);
  const idDoTitulo = useId();

  useEffect(() => {
    const dialogo = referencia.current;
    if (!dialogo) return;
    if (aberto && !dialogo.open) dialogo.showModal();
    if (!aberto && dialogo.open) dialogo.close();
  }, [aberto]);

  /**
   * Prende a rolagem da página enquanto o diálogo está aberto.
   *
   * O `<dialog>` nativo torna o resto da página inerte ao CLIQUE e ao teclado,
   * mas não à RODA do mouse: rolando dentro de um formulário longo, ao chegar no
   * fim do modal a rolagem seguia para a lista atrás — a página corria centenas
   * de pixels e, ao fechar, ninguém estava mais onde tinha parado. Com o
   * formulário de devedor, que é o mais longo, a página ia a 3000 px.
   */
  useEffect(() => {
    if (!aberto) return;
    dialogosAbertos += 1;
    document.body.style.overflow = 'hidden';
    return () => {
      dialogosAbertos -= 1;
      if (dialogosAbertos === 0) document.body.style.overflow = '';
    };
  }, [aberto]);

  return (
    <dialog
      ref={referencia}
      aria-labelledby={idDoTitulo}
      onCancel={(e) => {
        e.preventDefault();
        aoFechar();
      }}
      /* Fechar ao clicar fora. É `mouseDown` de propósito: com `click`, arrastar
         uma seleção de texto de dentro para fora fecharia o diálogo no meio. */
      onMouseDown={(e) => {
        if (e.target === referencia.current) aoFechar();
      }}
      className={
        'm-auto rounded-lg border bg-[color:var(--superficie)] p-0 text-[color:var(--texto)] ' +
        'shadow-2xl backdrop:bg-black/60'
      }
      style={{ width: `min(${largura}, calc(100vw - 2rem))` }}
    >
      <div className="flex max-h-[85dvh] flex-col">
        <header className="flex items-start justify-between gap-4 border-b px-6 py-4">
          <div>
            <h2 id={idDoTitulo} className="font-serif text-lg text-[color:var(--texto)]">
              {titulo}
            </h2>
            {descricao && (
              <p className="mt-0.5 text-sm text-[color:var(--texto-suave)]">{descricao}</p>
            )}
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="-mr-1 rounded px-2 py-0.5 text-xl leading-none text-[color:var(--texto-tenue)] transition-colors hover:text-[color:var(--texto)]"
          >
            ×
          </button>
        </header>

        {/* `overscroll-contain`: ao chegar no fim do formulário, a roda para aqui
            em vez de continuar na página atrás. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
          {children}
        </div>

        {rodape && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t bg-[color:var(--superficie-afundada)] px-6 py-4">
            {rodape}
          </footer>
        )}
      </div>
    </dialog>
  );
}
